import { call, put, take, spawn, takeEvery, all } from 'redux-saga/effects';
import { serverTimestamp } from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import types from './actionTypes';
import * as authActions from './actions';
import * as subscriptionsActions from '../subscriptions/actions';
import * as invoicesActions from '../invoices/actions';
import * as affiliatesActions from '../affiliates/actions';
import * as commissionsActions from '../commissions/actions';
import * as discountCodesActions from '../discountCodes/actions';
import * as usersActions from '../users/actions';
import * as dashboardActions from '../dashboard/actions';

import { toDateFirebase } from '../../helpers/sharedFunction';
import { trackLogin, trackSignup } from '../../lib/analytics';
import roles from '../../config/roles';
import getAuthError from '../../config/auth';

function* loginWithEmailSaga({ email, password, history }) {
  try {
    const { user } = yield call(
      rsf.auth.signInWithEmailAndPassword,
      email,
      password,
    );

    try {
      trackLogin('email');
    } catch (trackingError) {
      console.warn('trackLogin error:', trackingError);
    }
    yield call(fetchAdminSaga, user.uid, history);
  } catch (error) {
    const message = getAuthError(error.code);
    yield put(authActions.loginWithEmailFailure({ message }));
  }
}

function* registerWithEmailSaga({ user: userData, history }) {
  try {
    const { user } = yield call(
      rsf.auth.createUserWithEmailAndPassword,
      userData.email,
      userData.password,
    );

    // we can't delete the password because it's used in the ICC API
    //delete userData.password;
    delete userData.confirmPassword;

    yield call(createAdminSaga, user.uid, userData, 'email', history);
  } catch (error) {
    const message = getAuthError(error.code);
    yield put(authActions.registerWithEmailFailure({ message }));
  }
}

function* logoutSaga({ history }) {
  try {
    yield put(subscriptionsActions.resetState());
    yield put(invoicesActions.resetState());
    yield put(affiliatesActions.resetState());
    yield put(commissionsActions.resetState());
    yield put(discountCodesActions.resetState());
    yield put(usersActions.resetState());
    yield put(dashboardActions.resetState());
    yield call(rsf.auth.signOut);
    yield put(authActions.logoutSuccess());
    history.push('/index');
  } catch (error) {
    yield put(authActions.logoutFailure(error));
  }
}

function* syncAdminSaga() {
  const channel = yield call(rsf.auth.channel);

  while (true) {
    const { error, user } = yield take(channel);

    if (error) {
      console.error('Auth channel error:', error);
      break;
    }

    // Extract only serializable properties from the Firebase User object.
    // The full object contains circular references (ComponentContainer)
    // that crash Reactotron's serializer and redux-persist's IndexedDB storage.
    const credential = user
      ? { uid: user.uid, email: user.email, displayName: user.displayName }
      : null;

    yield put(authActions.syncAdmin(credential));
  }
}

function* fetchAdminSaga(payloadOrUserId, maybeHistory) {
  const isActionObject =
    payloadOrUserId && typeof payloadOrUserId === 'object' && !('length' in payloadOrUserId);
  const userId = isActionObject
    ? payloadOrUserId?.credential?.uid || payloadOrUserId?.credential?.id
    : payloadOrUserId;
  const history = isActionObject ? payloadOrUserId?.history : maybeHistory;

  try {
    if (!userId) {
      throw new Error('Missing userId while fetching admin profile');
    }

    const adminSnap = yield call(rsf.firestore.getDocument, `users/${userId}`);
    const data = adminSnap.data();

    const admin = {
      id: userId,
      ...data,
      createdAt: toDateFirebase(adminSnap, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(adminSnap, data, 'updatedAt').toDate(),
      }),
    };
    if (admin.role && admin.active) {
      yield put(authActions.fetchAdminSuccess(admin, ''));
      if (history && typeof history.push === 'function') {
        history.push('/dashboard');
      }
    } else {
      yield put(
        authActions.fetchAdminSuccess(
          null,
          'Contact your administrator to request access',
        ),
      );
    }
  } catch (error) {
    yield put(authActions.fetchAdminFailure(error));
  }
}

function* createAdminSaga(id, user, provider, history) {
  try {
    const admin = {
      active: true,
      provider,
      role: roles.SUBSCRIBER,
      checkIccUser: 'new', //we need to check if the user exists in the ICC DB
      ...user,
      createdAt: serverTimestamp(),
    };
    yield call(rsf.firestore.setDocument, `users/${id}`, admin);
    yield put(authActions.fetchAdminSuccess({ id, ...admin }, ''));

    try {
      trackSignup('email');
    } catch (trackingError) {
      console.warn('trackSignup error:', trackingError);
    }

    history.push('/dashboard');
  } catch (error) {
    yield put(authActions.createAdminFailure(error));
  }
}

function* passwordChangeSaga({ password }) {
  try {
    yield call(rsf.auth.updatePassword, password);
    yield put(authActions.passwordChangeSuccess());
  } catch (error) {
    yield put(authActions.passwordChangeFailure(error));
  }
}

function* passwordForgetSaga({ email }) {
  try {
    yield call(rsf.auth.sendPasswordResetEmail, email);
    yield put(
      authActions.passwordForgetSuccess(
        'Reset link are sended to your mailbox, check there first',
      ),
    );
  } catch (error) {
    yield put(authActions.passwordForgetFailure(error));
  }
}

export default function* authSaga() {
  // spawn instead of fork: syncAdminSaga runs detached — its errors/cancellation
  // never propagate to authSaga or the other takeEvery watchers.
  yield spawn(syncAdminSaga);
  yield all([
    takeEvery(types.LOGIN_WITH_EMAIL.REQUEST, loginWithEmailSaga),
    takeEvery(types.REGISTER_WITH_EMAIL.REQUEST, registerWithEmailSaga),
    takeEvery(types.FETCH_ADMIN.REQUEST, fetchAdminSaga),
    takeEvery(types.LOGOUT.REQUEST, logoutSaga),
    takeEvery(types.PASSWORD_CHANGE.REQUEST, passwordChangeSaga),
    takeEvery(types.PASSWORD_FORGET.REQUEST, passwordForgetSaga),
  ]);
}
